import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { 
  ArrowRight,
  Sparkles,
  GitBranch,
  Target,
  RefreshCw,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEdgeFunctionRetry } from "@/hooks/useEdgeFunctionRetry";
import { ErrorRecovery } from "@/components/ui/error-recovery";

interface Phase2TransitionProps {
  sessionId: string;
  problemStatement: string;
  tensions: string[];
  primaryHypothesis?: string;
  decisionQuestion?: string;
  sessionMeta?: Record<string, any> | null;
  onError: (message: string) => void;
}

const formatEur = (val?: number) => {
  if (val == null) return null;
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);
};

export const Phase2Transition = ({
  sessionId,
  problemStatement,
  tensions,
  primaryHypothesis,
  decisionQuestion,
  sessionMeta,
  onError,
}: Phase2TransitionProps) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"preparing" | "generating" | "ready" | "error">("preparing");
  const [statusMessage, setStatusMessage] = useState("Entscheidungsrahmen wird analysiert...");
  const [acknowledged, setAcknowledged] = useState(false);

  const { execute, state: retryState } = useEdgeFunctionRetry<void>();

  // Extract data from sessionMeta
  const thesis = sessionMeta?.phase1_4_thesis || sessionMeta?.decision_thesis;
  const irr = sessionMeta?.phase1_3_irreversibility;
  const conflict = sessionMeta?.phase1_2_conflict;
  const onboardingCtx = sessionMeta?.onboarding_context;

  const kernsatz = thesis?.kernsatz || primaryHypothesis;
  const killCriterion = thesis?.killCriterion || thesis?.kill_criterion;
  const urgencyScore = irr?.structural_urgency_score ?? irr?.structuralUrgencyScore;
  const urgencyLevel = irr?.structural_urgency_level ?? irr?.structuralUrgencyLevel ?? 
    (urgencyScore != null ? (urgencyScore >= 7 ? "red" : urgencyScore >= 4 ? "yellow" : "green") : null);
  const dailyExposure = irr?.daily_exposure ?? irr?.dailyExposure;
  const exposureUntilValidation = irr?.exposure_until_validation ?? irr?.exposureUntilValidation;
  const validationDeadlineDays = irr?.validation_deadline_days ?? irr?.validationDeadlineDays;
  const lockInTypes = irr?.lock_in_types ?? irr?.lockInTypes ?? [];
  const commitDecision13 = irr?.commit_decision ?? irr?.commitDecision;

  const optionA = onboardingCtx?.optionA?.label || onboardingCtx?.option_a_label;
  const optionB = onboardingCtx?.optionB?.label || onboardingCtx?.option_b_label;

  // Parse kernsatz into structured parts
  const parsedThesis = useMemo(() => {
    if (!kernsatz) return null;
    const text = kernsatz;
    
    // Try splitting by "Annahmen:" / "Risiko:"
    const assumptionMatch = text.match(/(?:Annahmen?|Kernannahmen?):\s*([\s\S]*?)(?=(?:Risiko|Dominantes Risiko):|$)/i);
    const riskMatch = text.match(/(?:Risiko|Dominantes Risiko):\s*([\s\S]*?)$/i);
    
    let bet = text;
    let assumptions: string[] = [];
    let risk = "";

    if (assumptionMatch) {
      bet = text.substring(0, text.indexOf(assumptionMatch[0])).replace(/(?:Annahmen?|Kernannahmen?):\s*$/i, "").trim();
      assumptions = assumptionMatch[1].split(/\n|•|–/).map(s => s.trim()).filter(Boolean);
    }
    if (riskMatch) {
      risk = riskMatch[1].trim();
      if (!assumptionMatch) {
        bet = text.substring(0, text.indexOf(riskMatch[0])).trim();
      }
    }

    // If no structure found, use first sentence as bet
    if (!assumptionMatch && !riskMatch) {
      const sentences = text.split(/(?<=\.)\s+/);
      if (sentences.length > 1) {
        bet = sentences[0];
        risk = sentences.slice(1).join(" ");
      }
    }

    return { bet, assumptions, risk };
  }, [kernsatz]);

  const hasStrategicData = !!(kernsatz || killCriterion || urgencyScore != null || dailyExposure);

  const phase2Preview = [
    { icon: GitBranch, title: "Strategische Optionen", description: "Drei unterschiedliche Lösungswege basierend auf deinem Entscheidungsrahmen" },
    { icon: Sparkles, title: "KI-Tiefenanalyse", description: "Risiken, Chancen und strategischer Fit pro Option" },
    { icon: Target, title: "Vergleich und Empfehlung", description: "Datenbasierte Entscheidungsgrundlage mit Szenario-Bewertung" },
  ];

  const callPhase2Start = useCallback(async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Nicht authentifiziert");
    const response = await supabase.functions.invoke("phase2-start", {
      body: { session_id: sessionId },
    });
    if (response.error) throw new Error(response.error.message || "Fehler beim Generieren");
  }, [sessionId]);

  const startPhase2Generation = useCallback(async () => {
    try {
      setStatus("preparing");
      setStatusMessage("Entscheidungsrahmen wird analysiert...");
      setProgress(0);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setStatus("generating");
      setStatusMessage("Strategische Optionen werden generiert...");
      setProgress(25);

      await execute(callPhase2Start);

      setProgress(95);
      setStatusMessage("Optionen bereit.");
      
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setStatus("ready");
      setProgress(100);
    } catch (error: any) {
      console.error("Phase 2 generation error:", error);
      setStatus("error");
      onError(error.message || "Fehler beim Starten von Phase 2");
    }
  }, [execute, callPhase2Start, onError]);

  useEffect(() => {
    startPhase2Generation();
  }, []);

  useEffect(() => {
    if (status === "preparing") {
      const timer = setTimeout(() => setProgress(15), 300);
      return () => clearTimeout(timer);
    }
    if (status === "generating") {
      const interval = setInterval(() => {
        setProgress(prev => prev >= 85 ? prev : prev + Math.random() * 6);
      }, 900);
      return () => clearInterval(interval);
    }
    if (status === "ready") setProgress(100);
  }, [status]);

  const handleContinue = () => {
    navigate(`/app/session/${sessionId}/phase2`);
  };

  const urgencyColor = urgencyLevel === "red" 
    ? "text-destructive" 
    : urgencyLevel === "yellow" 
      ? "text-amber-500" 
      : "text-emerald-500";

  const urgencyBg = urgencyLevel === "red" 
    ? "bg-destructive/10 border-destructive/20" 
    : urgencyLevel === "yellow" 
      ? "bg-amber-500/10 border-amber-500/20" 
      : "bg-emerald-500/10 border-emerald-500/20";

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center overflow-y-auto">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 50% at 20% 20%, hsl(var(--primary) / 0.06) 0%, transparent 50%),
              radial-gradient(ellipse 60% 60% at 80% 80%, hsl(var(--primary) / 0.04) 0%, transparent 50%)
            `,
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.012]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8">
        
        {/* ── Loading / Generating State ── */}
        {(status === "preparing" || status === "generating") && (
          <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-2xl animate-fade-in">
            {/* Phase badge */}
            <div className="flex items-center gap-2 mb-8">
              <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Phase 1 abgeschlossen
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Dein Entscheidungsrahmen steht.
            </h1>
            <p className="text-muted-foreground mb-8">
              Die KI entwickelt jetzt drei strategische Optionen auf Basis deiner Analyse.
            </p>

            {/* Thesis preview during loading */}
            {parsedThesis && (
              <div className="mb-8 p-5 rounded-xl border border-border/40 bg-muted/10">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                  Strategische Wette
                </p>
                <p className="text-base font-semibold text-foreground leading-relaxed">
                  {parsedThesis.bet}
                </p>
                {killCriterion && (
                  <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                    <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-destructive/70" />
                    <span>Kipppunkt: {killCriterion}</span>
                  </div>
                )}
              </div>
            )}

            {/* Retry banner */}
            {(retryState.isRetrying || retryState.isOffline) && (
              <div className="mb-6">
                <ErrorRecovery
                  error={null}
                  isRetrying={retryState.isRetrying}
                  retryCount={retryState.retryCount}
                  isOffline={retryState.isOffline}
                  onRetry={startPhase2Generation}
                  compact
                />
              </div>
            )}

            {/* Progress */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">
                  {retryState.isRetrying 
                    ? `Automatischer Retry ${retryState.retryCount}/3...` 
                    : statusMessage}
                </p>
                <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-1.5" />
            </div>

            {/* What's next */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                Was in Phase 2 auf dich wartet
              </p>
              {phase2Preview.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={index}
                    className="flex items-start gap-3 text-sm animate-fade-in"
                    style={{ animationDelay: `${400 + index * 150}ms` }}
                  >
                    <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{item.title}</span>
                      <p className="text-muted-foreground text-xs mt-0.5">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Ready State: Executive Briefing ── */}
        {status === "ready" && (
          <div className="space-y-4 animate-fade-in">
            
            {/* Header card */}
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-6 md:p-8 shadow-2xl">
              <div className="flex items-center gap-2 mb-6">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-wider text-primary">
                  Phase 2 bereit
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Drei strategische Optionen generiert.
              </h1>
              <p className="text-muted-foreground">
                Basierend auf deinem Entscheidungsrahmen hat die KI drei differenzierte Lösungswege entwickelt.
              </p>
            </div>

            {/* Strategic summary card */}
            {hasStrategicData && (
              <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-6 md:p-8 shadow-lg">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
                  Dein Entscheidungsrahmen
                </p>

                {/* Thesis */}
                {parsedThesis && (
                  <div className="mb-5">
                    <p className="text-base md:text-lg font-semibold text-foreground leading-relaxed">
                      {parsedThesis.bet}
                    </p>
                    {parsedThesis.assumptions.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {parsedThesis.assumptions.map((a, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/50 mt-1.5 flex-shrink-0" />
                            {a}
                          </li>
                        ))}
                      </ul>
                    )}
                    {parsedThesis.risk && (
                      <div className="mt-3 flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-destructive/70" />
                        <span className="text-muted-foreground">{parsedThesis.risk}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Key metrics row */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                  {/* Options */}
                  {(optionA || optionB) && (
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">Optionen</p>
                      <div className="space-y-1">
                        {optionA && <p className="text-xs font-medium text-foreground truncate">A: {optionA}</p>}
                        {optionB && <p className="text-xs font-medium text-foreground truncate">B: {optionB}</p>}
                      </div>
                    </div>
                  )}

                  {/* Urgency */}
                  {urgencyScore != null && (
                    <div className={cn("p-3 rounded-lg border", urgencyBg)}>
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">Strukturelle Dringlichkeit</p>
                      <p className={cn("text-lg font-bold", urgencyColor)}>{urgencyScore}/10</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {urgencyLevel === "red" ? "Kritisch" : urgencyLevel === "yellow" ? "Eingeschränkt" : "Spielraum"}
                      </p>
                    </div>
                  )}

                  {/* Kill criterion */}
                  {killCriterion && (
                    <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/15">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">Kipppunkt</p>
                      <p className="text-xs text-foreground line-clamp-2">{killCriterion}</p>
                    </div>
                  )}

                  {/* Validation deadline */}
                  {validationDeadlineDays != null && (
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Validierungsfrist</p>
                      </div>
                      <p className="text-lg font-bold text-foreground">{validationDeadlineDays} Tage</p>
                    </div>
                  )}

                  {/* Daily exposure */}
                  {dailyExposure != null && (
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <TrendingUp className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Burn pro Tag</p>
                      </div>
                      <p className="text-lg font-bold text-foreground">{formatEur(dailyExposure)}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">ohne Validierung</p>
                    </div>
                  )}

                  {/* Total exposure */}
                  {exposureUntilValidation != null && (
                    <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Zap className="w-3 h-3 text-muted-foreground" />
                        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Exposition gesamt</p>
                      </div>
                      <p className="text-lg font-bold text-foreground">{formatEur(exposureUntilValidation)}</p>
                    </div>
                  )}
                </div>

                {/* Lock-in badges */}
                {lockInTypes.length > 0 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Lock-In:</span>
                    {lockInTypes.map((type: string) => (
                      <span key={type} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 border border-border/40 text-muted-foreground font-medium capitalize">
                        {type.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CTA card */}
            <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-6 md:p-8 shadow-lg">
              {/* Acknowledgment checkbox for high-risk */}
              {(urgencyLevel === "red" || urgencyLevel === "yellow") && (
                <label className="flex items-start gap-3 mb-5 cursor-pointer group">
                  <Checkbox 
                    checked={acknowledged}
                    onCheckedChange={(v) => setAcknowledged(!!v)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors leading-relaxed">
                    {urgencyLevel === "red" 
                      ? `Ich nehme zur Kenntnis, dass wir eine Exposition von ${formatEur(exposureUntilValidation) || "unbekannt"} bei ${lockInTypes.length} aktiven Lock-In-Dimensionen akzeptieren.`
                      : `Ich bestätige, dass die strategischen Risiken geprüft sind und wir zur Validierung innerhalb von ${validationDeadlineDays || "?"} Tagen committen.`
                    }
                  </span>
                </label>
              )}

              <Button 
                onClick={handleContinue} 
                className="w-full gap-2 h-12 text-base"
                size="lg"
                disabled={(urgencyLevel === "red" || urgencyLevel === "yellow") && !acknowledged}
              >
                Optionen erkunden
                <ArrowRight className="w-5 h-5" />
              </Button>

              <p className="text-center text-xs text-muted-foreground mt-4">
                Drei differenzierte Lösungswege warten auf deine Bewertung.
              </p>
            </div>
          </div>
        )}

        {/* ── Error State ── */}
        {status === "error" && (
          <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-2xl animate-fade-in">
            <ErrorRecovery
              error={retryState.lastError || "Es gab ein Problem beim Generieren der Optionen."}
              isRetrying={retryState.isRetrying}
              retryCount={retryState.retryCount}
              isOffline={retryState.isOffline}
              onRetry={startPhase2Generation}
            />
            
            {!retryState.isRetrying && !retryState.isOffline && (
              <div className="mt-6">
                <Button 
                  onClick={startPhase2Generation} 
                  className="w-full gap-2"
                  size="lg"
                >
                  <RefreshCw className="w-4 h-4" />
                  Erneut versuchen
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
